/**
 * Retires a key -- `POST /ops/keys/{k}/retire`, no body: the api refuses a
 * key still named by any operation's or inference pairing's `used_by`
 * (409) rather than taking a grace window from the caller, so this dialog
 * is a plain confirm.
 */

import { useState, type ReactNode } from 'react';

import { ApiFailureError, type ApiFailure } from '../../lib/http/errors';
import { defineCatalogue } from '../../i18n';
import { useRetireKey } from '../api/queries';
import { ConfirmDialog } from '../../chrome/form';

type RetireDialogKey =
  | 'retire.confirm.title'
  | 'retire.confirm.body'
  | 'retire.confirm.cancel'
  | 'retire.confirm.confirm';

const catalogue = defineCatalogue<Record<RetireDialogKey, string>>(
  {
    'retire.confirm.title': 'Aposentar esta chave?',
    'retire.confirm.body':
      'O segredo armazenado é excluído em seguida, e o registro permanece. Uma chave ainda ' +
      'usada por alguma operação não pode ser aposentada.',
    'retire.confirm.cancel': 'Cancelar',
    'retire.confirm.confirm': 'Aposentar',
  },
  {
    'retire.confirm.title': 'Retire this key?',
    'retire.confirm.body':
      'The stored secret is deleted right away, and the record stays. A key still used by an ' +
      'operation cannot be retired.',
    'retire.confirm.cancel': 'Cancel',
    'retire.confirm.confirm': 'Retire',
  },
);
const kt = catalogue.t;

export function RetireKeyDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyId: string;
}): ReactNode {
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const mutation = useRetireKey();

  function handleConfirm(): Promise<unknown> {
    setFailure(null);
    return mutation
      .mutateAsync({ keyId: props.keyId })
      .catch((error: unknown) => {
        setFailure(
          error instanceof ApiFailureError ? error.failure : { kind: 'server', status: 500 },
        );
        throw error;
      })
      .finally(() => mutation.reset());
  }

  return (
    <ConfirmDialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) setFailure(null);
        props.onOpenChange(open);
      }}
      title={kt('retire.confirm.title')}
      description={kt('retire.confirm.body')}
      confirmLabel={kt('retire.confirm.confirm')}
      cancelLabel={kt('retire.confirm.cancel')}
      pending={mutation.isPending}
      failure={failure}
      onConfirm={handleConfirm}
      destructive
    />
  );
}
